/**
 * The crucible page's scene: one game session, started the moment the page
 * knows which crucible it is on, drawing the gathering into the shared game
 * canvas (behind the page's UI) and later the run, on the same scene. This
 * hook starts it, mounts its canvas and turns party state into gathering
 * scene commands (the entry countdown's beats included). A solo
 * direct-entry run starts the scene without the gathering: it only loads
 * (`useRunModelPreload`).
 */
import {
    ensureCrucibleSession,
    GAME_CANVAS_MOUNT_SELECTOR,
    mountGameCanvas,
    sendGatheringCommand,
    subscribeSceneMessages,
    useGameSession,
    type GameSessionHandle,
} from '@sorrem-client/game/game-session-store';
import type { CrucibleGatheringRosterMemberState, GatheringEntranceSpot } from '@sorrem-client/game/render-config';
import { usePrefersReducedMotion } from '@sorrem-client/ui/hooks';
import { createLogger, type CrucibleRecord } from '@sorrem/shared';
import { useEffect, useState } from 'react';
import type { RosterMemberKey } from './use-crucible-roster';

const logger = createLogger('client:crucible-scene');

export interface CrucibleSceneInput {
    /** Whether the scene draws the gathering (false for a solo direct-entry run). */
    readonly hasGathering: boolean;
    /** The crucible, or null until the party state arrives; the scene starts loading without it. */
    readonly crucible: Pick<CrucibleRecord, 'id' | 'maxPlayers' | 'veilSaturation' | 'location'> | null;
    readonly roster: readonly CrucibleGatheringRosterMemberState[];
    readonly joinedMembers: readonly RosterMemberKey[];
    readonly leftMembers: readonly RosterMemberKey[];
    /** Whole seconds left on the entry countdown, or null when none runs: the entrance beats on each (#266). */
    readonly countdownSecondsRemaining: number | null;
}

export interface CrucibleScene {
    /** The session, once started; its host starts the run on the same scene. */
    readonly session: GameSessionHandle | undefined;
    /** Why the scene failed, or null. */
    readonly sceneError: string | null;
    /** The gathering set is built for this crucible, so the page can show it. */
    readonly isGatheringBuilt: boolean;
    /** Where the entry countdown stands over the entrance and how big the entrance is drawn; null until the gathering is built. */
    readonly entrance: GatheringEntranceSpot | null;
}

/** `crucibleId` is null until the page knows the crucible and whether it gathers; the scene starts then. */
export function useCrucibleScene(crucibleId: string | null, { hasGathering, crucible, roster, joinedMembers, leftMembers, countdownSecondsRemaining }: CrucibleSceneInput): CrucibleScene {
    // Subscribed before the effect below starts the session, so its notify isn't missed.
    const session = useGameSession();
    const [sceneError, setSceneError] = useState<string | null>(null);
    const [isGatheringBuilt, setIsGatheringBuilt] = useState(false);
    const [entrance, setEntrance] = useState<GatheringEntranceSpot | null>(null);

    useEffect(() => subscribeSceneMessages((message) => {
        switch (message.type) {
            case 'run-models-ready':
                // Counted by useRunModelPreload.
                return;
            case 'gathering-ready':
                setIsGatheringBuilt(true);
                setEntrance(message.entrance);
                return;
            case 'scene-error':
                logger.error('crucible scene error:', message.message);
                setSceneError(message.message);
                return;
            default: {
                const unhandled: never = message;
                logger.error('unhandled crucible scene message:', unhandled);
            }
        }
    }), []);

    // Never torn down here: the session outlives the gathering UI into the
    // run, and leaving the page is a full page load. See game-session-store.ts.
    useEffect(() => {
        if (crucibleId === null) {
            return;
        }
        ensureCrucibleSession(crucibleId, hasGathering);
        mountGameCanvas(GAME_CANVAS_MOUNT_SELECTOR);
    }, [crucibleId, hasGathering]);

    const id = crucible?.id ?? null;
    const maxPlayers = crucible?.maxPlayers ?? null;
    const veilSaturation = crucible?.veilSaturation ?? null;
    const location = crucible?.location ?? null;
    useEffect(() => {
        if (!hasGathering || session === undefined || id === null || maxPlayers === null || veilSaturation === null || location === null) {
            return;
        }
        // Sent once the party state brings the crucible; roster-derived
        // visuals update via `crucible-gathering-set-roster` below instead.
        sendGatheringCommand({ type: 'crucible-gathering-init', crucibleId: id, maxPlayers, veilSaturation, location });
    }, [hasGathering, session, id, maxPlayers, veilSaturation, location]);

    useEffect(() => {
        if (session !== undefined) {
            sendGatheringCommand({ type: 'crucible-gathering-set-roster', members: roster });
        }
    }, [session, roster]);

    useEffect(() => {
        if (session === undefined) {
            return;
        }
        for (const { accountId, characterId } of joinedMembers) {
            sendGatheringCommand({ type: 'crucible-gathering-join-event', accountId, characterId });
        }
    }, [session, joinedMembers]);

    useEffect(() => {
        if (session === undefined) {
            return;
        }
        for (const { accountId, characterId } of leftMembers) {
            sendGatheringCommand({ type: 'crucible-gathering-leave-event', accountId, characterId });
        }
    }, [session, leftMembers]);

    const isReducedMotion = usePrefersReducedMotion();
    useEffect(() => {
        if (hasGathering && session !== undefined) {
            sendGatheringCommand({ type: 'crucible-gathering-countdown', secondsRemaining: countdownSecondsRemaining, isReducedMotion });
        }
    }, [hasGathering, session, countdownSecondsRemaining, isReducedMotion]);

    return { session, sceneError, isGatheringBuilt, entrance };
}
